// Shared Spend & Save display window. Checkout discounts remain separately configured.
class ElkSpendSavePromotion extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.content = this.querySelector('[data-spend-save-content]');
    this.note = this.querySelector('[data-pdp-countdown-editor-note]');
    this.panel = this.querySelector('[data-pdp-countdown-panel]');
    this.fallback = this.querySelector('[data-pdp-countdown-fallback]');
    this.accessible = this.querySelector('[data-pdp-countdown-accessible]');
    this.message = this.querySelector('[data-pdp-countdown-message]');
    this.countdownEnabled = this.dataset.countdownEnabled === 'true';
    this.parts = ['days', 'hours', 'minutes', 'seconds'].map((part) =>
      this.querySelector(`[data-pdp-countdown-${part}]`)
    );
    if (!this.content || (this.countdownEnabled && (!this.panel || !this.accessible || !this.message || this.parts.some((part) => !part)))) {
      this.hidden = true;
      return;
    }
    if (this.dataset.enabled !== 'true') {
      this.showInactive('Spend & Save is off. Enable it in Theme settings > Spend & save promotion settings.');
      return;
    }

    if (this.dataset.tiersValid === 'false') {
      this.showInactive('Enter two valid tiers in Theme settings > Spend & save promotion settings. Amounts must be positive AUD values with at most two decimal places. Tier 2 must have a higher threshold and saving; each saving must be below its threshold.');
      return;
    }

    this.startAt = this.parseTimestamp(this.dataset.startAt);
    this.endAt = this.parseTimestamp(this.dataset.endAt);
    if (this.startAt === null || this.endAt === null || this.endAt <= this.startAt) {
      this.showInactive('Enter valid start and end dates with timezone offsets in Spend & save promotion settings. The end must be after the start.');
      return;
    }

    const serverSeconds = this.dataset.serverSeconds;
    if (!/^\d+$/.test(serverSeconds || '') || Number(serverSeconds) <= 0) {
      this.showInactive('Spend & Save could not read the store time. Reload the page to try again.');
      return;
    }
    // Moving a section must not restart its server-anchored schedule.
    if (!Number.isFinite(this.clockOffset) || this.clockSource !== serverSeconds) {
      this.clockOffset = Number(serverSeconds) * 1000 - Date.now();
      this.clockSource = serverSeconds;
    }
    this.handlePageShow = () => this.update();
    this.handleVisibilityChange = () => { if (!document.hidden) this.update(); };
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.update();
  }

  disconnectedCallback() {
    this.stopTimer();
    if (this.handlePageShow) window.removeEventListener('pageshow', this.handlePageShow);
    if (this.handleVisibilityChange) document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  parseTimestamp(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([+-])(\d{2}):(\d{2})$/.exec(value?.trim() || '');
    if (!match) return null;
    const [, year, month, day, hour, minute, second, , offsetHour, offsetMinute] = match;
    if (
      Number(year) < 1000 ||
      Number(month) < 1 || Number(month) > 12 ||
      Number(day) < 1 || Number(day) > new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate() ||
      Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59 ||
      Number(offsetHour) > 14 || Number(offsetMinute) > 59 ||
      (Number(offsetHour) === 14 && Number(offsetMinute) !== 0)
    ) return null;
    const timestamp = Date.parse(match[0]);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  stopTimer() {
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  scheduleUpdate(delay) {
    this.stopTimer();
    this.timer = window.setTimeout(() => this.update(), delay);
  }

  setPromotionActive(active) {
    const previous = this.promotionActive;
    this.promotionActive = active;
    // Initial cart rendering already loads recommendations. Only notify transitions.
    if (previous !== undefined && previous !== active) {
      this.dispatchEvent(new CustomEvent('elk:spend-save-state-change', {bubbles: true, detail: {active}}));
    }
  }

  showInactive(message) {
    this.content.hidden = true;
    if (this.panel) this.panel.hidden = true;
    if (this.accessible) this.accessible.textContent = '';
    const showNote = this.dataset.editor === 'true' && Boolean(this.note);
    this.hidden = !showNote;
    if (this.note) {
      this.note.hidden = !showNote;
      this.note.textContent = message;
    }
    this.setPromotionActive(false);
  }

  update() {
    this.stopTimer();
    const now = Date.now() + this.clockOffset;
    if (now < this.startAt) {
      this.showInactive('Spend & Save is scheduled. It will appear on the storefront at the configured start time.');
      this.scheduleUpdate(Math.min(this.startAt - now, 60000));
      return;
    }
    const remaining = this.endAt - now;
    if (remaining <= 0) {
      this.showInactive('Spend & Save has ended and is hidden on the storefront. Set a new schedule to use it again.');
      return;
    }

    if (this.countdownEnabled) {
      const seconds = Math.ceil(remaining / 1000);
      const values = [Math.floor(seconds / 86400), Math.floor((seconds % 86400) / 3600), Math.floor((seconds % 3600) / 60), seconds % 60];
      this.parts.forEach((part, index) => { part.textContent = String(values[index]).padStart(2, '0'); });
      const units = ['days', 'hours', 'minutes', 'seconds'];
      this.accessible.textContent = `${this.message.textContent.trim()}: ${values.map((value, index) => `${value} ${units[index]}`).join(', ')}`;
      this.panel.hidden = false;
      if (this.fallback) this.fallback.hidden = true;
    }
    this.hidden = false;
    this.content.hidden = false;
    if (this.note) this.note.hidden = true;
    this.setPromotionActive(true);
    this.scheduleUpdate(Math.min(this.countdownEnabled ? 1000 : 60000, remaining));
  }
}

class ElkPdpCountdown extends ElkSpendSavePromotion {}

if (!customElements.get('elk-spend-save-promotion')) customElements.define('elk-spend-save-promotion', ElkSpendSavePromotion);
if (!customElements.get('elk-pdp-countdown')) customElements.define('elk-pdp-countdown', ElkPdpCountdown);
